<template>
  <v-row no-gutters>
    <v-col cols="12" lg="8" md="8">
      <p class="text-h2">
        {{ canEditOffer ? $t('STEPPER_STEP1_H1') : $t('STEPPER_STEP1_H1_READONLY') }}
      </p>

      <p v-if="canEditOffer" class="text-body-1 grey-font mb-8">
        {{ $t('STEPPER_STEP1_DESCRIPTION') }}
      </p>

      <p class="text-h3 grey-font">{{ $t('STEPPER_STEP1_H3') }}</p>

      <div class="mt-4 d-flex">
        <template v-if="mandatoryItems.length">
          <div class="d-flex flex-column products__mandatory-item" 
            v-for="(mandatoryCol, index) in 3" 
            :key="'mandatory-col-' + index">
            <p :class="`text-h4 grey-font mt-2 ${optionaCheckboxesClass(index)}`"
              v-for="(item, index2) in dividedMandatories(index)" 
              :key="'Mandatory-' + index2">
              <v-icon color="primary">mdi-check</v-icon> {{ getProductLabel(item) }}
            </p>
          </div>
        </template>
        <template v-else>
          <p class="text-body-1 grey-font mb-8">
            {{ $t('STEPPER_STEP1_DESCRIPTION2') }}
          </p>
        </template>
      </div>

      <p v-if="hasOptionalProducts" class="text-h3 grey-font mt-8">
        {{ $t('STEPPER_STEP1_OPTIONAL') }}
      </p>

      <v-checkbox v-if="hasOptionalProducts" @change="emitCheckbox" @click="selectAll" class="mt-6 pl-2 text-h4 bold"
        :label="$t('STEPPER_STEP1_OPTIONAL_SELECTALL')" :value="$t('STEPPER_STEP1_OPTIONAL_SELECTALL')" hide-details />

      <div class=" mb-3 d-flex">
        <div class="d-flex flex-column" v-for="(col, index) in 3" :key="'optionalCategoryCol-' + index">
          <v-checkbox v-for="(item, productIndex) in dividedOptionals(index)" @change="emitCheckbox"
            v-model="optionalCheckboxes" :class="`text-h4 grey-font mt-2 ${optionaCheckboxesClass(index)}`"
            :key="'checkbox-' + productIndex" :label="$t(item.desc_key)" :value="item.id" :readonly="!canEditOffer"
            hide-details />
        </div>
      </div>
    </v-col>

    <SummaryCard>
      <template v-slot:save-and-reset-offer-buttons>
        <SaveAndResetOfferButtons @onSaveClicked="saveOffer" @onResetClicked="resetOffer" />
      </template>

      <template v-slot:offer-status-button>
        <UpdateOfferStatusButton />
      </template>
    </SummaryCard>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import { UPDATE_OFFER_PRODUCT_SELECTION } from '@/store/modules/offer/actions'
import SummaryCard from '@/components/summary-card'
import SaveAndResetOfferButtons from '@/components/buttons/SaveAndResetOfferButtons'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'

export default {
  name: 'Products',
  components: {
    SummaryCard,
    SaveAndResetOfferButtons,
    UpdateOfferStatusButton,
  },
  data() {
    return {
      firstColumn: true,
      optionalCheckboxes: [],
      allChecked: false,
      emited: false,
      objectToSend: {},
      modifiedProductList: [],
    }
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM, 'canEditOffer']),
    getProductList() {
      return this.modifiedProductList.length > 0 ? this.modifiedProductList : this[GET_OFFER_ITEM]('productList');
    },
    getInternationalShipments() {
      return this[GET_OFFER_ITEM]('internationalShipments')
    },
    isRateModel2024() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2024"
    },
    isRateModel2025() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2025"
    },
    isRateModel2026() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026"
    },
    isRateModel2026V2() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026_V2"
    },
    shouldCombineC14E() {
      return this.isRateModel2024 || this.isRateModel2025 || this.isRateModel2026 || this.isRateModel2026V2
    },
    mandatoryItems() {
      const items = this.getProductList?.filter(this.isMandatory) || []
      // Ocultar C14E porque se muestra combinado con 24H
      if (this.shouldCombineC14E) {
        return items.filter(item => item.id !== 'C14E')
      }
      return items
    },
    optionalItems() {
      return this.getProductList?.filter(this.isOptional)
    },
    hasOptionalProducts() {
      return this.optionalItems?.length !== 0
    },
  },
  methods: {
    ...mapActions('offer', [UPDATE_OFFER_PRODUCT_SELECTION]),
    setProductSelection(ids) {
      this[UPDATE_OFFER_PRODUCT_SELECTION]({ ids })
    },
    saveOffer() {
      this.$emit('onSaveClicked')
    },
    resetOffer() {
      this.$emit('onResetClicked')
    },
    isMandatory(element) {
      return element.isMandatory
    },
    getProductLabel(item) {
      // Para 24H en modelos que combinan C14E, mostrar naming especial
      if (this.shouldCombineC14E && item.id === '24H') {
        return this.$t('PRODUCT_24H') + ' / ' + this.$t('PRODUCT_C14E')
      }
      return this.$t(`PRODUCT_${item.desc_key}`)
    },
    isOptional(element) {
      if ((element.id === "CIEL" || element.id === "ELIGHT") && this.getInternationalShipments < 500)
        return false
      return !element.isMandatory
    },
    optionaCheckboxesClass(index) {
      return index < 4 ? (index === 0 ? ' pl-2 pr-3 mr-12' : 'pr-3 mr-12') : null
    },
    dividedMandatories(index) {
      return this.groupItems(index, this.mandatoryItems)
    },
    dividedOptionals(index) {
      return this.groupItems(index, this.optionalItems)
    },
    emitCheckbox() {
      this.optionalCheckboxes.unshift(...this.mandatoryItems.map(element => element.id))
      this.emited = true
    },
    selectAll() {
      for (const producto in this.optionalItems) {
        this.allChecked
          ? (this.optionalCheckboxes = [])
          : this.optionalCheckboxes.push(this.optionalItems[producto].id)
      }
      this.allChecked = !this.allChecked
    },
    groupItems(index, itemsList) {
      const groups = itemsList.length / 3
      const integerGroup = Math.trunc(groups)
      const decimalGroup = groups - Math.trunc(groups)
      if (index === 0) {
        if (Number.isInteger(groups)) {
          return itemsList.slice(0, integerGroup)
        }

        return itemsList.slice(0, integerGroup + 1)
      }

      if (index === 1) {
        if (Number.isInteger(groups)) {
          return itemsList.slice(integerGroup, integerGroup * 2)
        }

        if (decimalGroup > 0.5) {
          return itemsList.slice(integerGroup + 1, (integerGroup + 1) * 2)
        }

        return itemsList.slice(integerGroup + 1, integerGroup * 2 + 1)
      }

      if (Number.isInteger(groups)) {
        return itemsList.slice(integerGroup * 2, itemsList.length)
      }

      if (decimalGroup > 0.5) {
        return itemsList.slice(integerGroup * 2 + 2, itemsList.length)
      }

      if (decimalGroup < 0.5) {
        return itemsList.slice(integerGroup * 2 + 1, itemsList.length)
      }
    },
    addPremiumProduct() {
      if ((this.isRateModel2024 || this.isRateModel2025 || this.isRateModel2026 || this.isRateModel2026V2) && this[GET_OFFER_ITEM]('productList')) {
        const productList = [...this[GET_OFFER_ITEM]('productList')];
        const product24hIndex = productList.findIndex(product => product.id === "24H");
        const premiumExists = productList.some(product => product.id === "C14E");
        if (product24hIndex !== -1 && !premiumExists) {
          const premium = { ...productList[product24hIndex] };
          premium.id = "C14E";
          productList.splice(product24hIndex + 1, 0, premium);
        }
        this.modifiedProductList = productList;
      } else {
        this.modifiedProductList = this[GET_OFFER_ITEM]('productList');
      }
    },
  },
  created() {
    this.addPremiumProduct();
    let selectedOptionals = this.optionalItems.filter(element => element.isPreSelected)
    if (selectedOptionals.length !== 0) {
      this.optionalCheckboxes = selectedOptionals.map(product => product.id)
    }
  },
  beforeDestroy() {
    if (!this.emited) {
      // rellenar el optionalcheckboxes cuando no se selecciona nada en los opcionales
      this.optionalCheckboxes.unshift(...this.mandatoryItems.map(element => element.id))
    }
    this.setProductSelection(this.optionalCheckboxes)
  },
}
</script>


<style scoped lang="scss">
.products {
  &__mandatory-item {
    min-width: rem(70);
  }
}
</style>
